import { styled, Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { Outlet, ScrollRestoration } from 'react-router-dom';

import { getPolicies } from '../../api/getPolicies';
import { useWelcomeQuery } from '../../api/getWelcomeDetails';
import { usePoliciesQuery } from '../../api/getPolicies';
import colors from '../../constants/colors';
import { usePageTrack, useIdentify } from '../../hooks/useAnalytics';
import useAuthCheck from '../../hooks/useAuthCheck';
import useCheckForLockedPolicies from '../../hooks/useCheckForLockedPolicies';
import useClearPaymentContext from '../../hooks/useClearPaymentContext';
import useGetCATBanner from '../../hooks/useGetCATBanner';
import Maintenance from '../../pages/Maintenance';
import { useSnackbarsContext } from '../../store/SnackbarProvider';
import { ChatProvider, useChatContext } from '../../store/chat';
import {
  editAccessibilityButtonStyles,
  setCookieWithExpiration,
} from '../../utils/helpers';
import Chat from '../Chat';
import MyPolicyFooter from '../Footer/MyPolicyFooter';
import Header from '../Header';
import PaperlessDialogs from '../PaperlessDialogs';
import SessionTimeoutModal from '../SessionTimeoutModal';
import Snackbar from '../Snackbar';
import Button from '../styledComponents/Button';
import CircularProgress from '../CircularProgress/CircularProgress';
import { AxiosError } from 'axios';

const Main = styled('main')({
  flex: 1,
  display: 'flex',
});

const isLocal = window.location.hostname.includes('localhost');

const disableChat = import.meta.env.VITE_CHAT_DISABLED === 'true';

const Layout: React.FC = () => {
  useIdentify();
  usePageTrack();
  useClearPaymentContext();
  useCheckForLockedPolicies();
  editAccessibilityButtonStyles();

  const { state } = useSnackbarsContext();
  const [isSkipLinkVisible, setIsSkipLinkVisible] = useState<boolean>(false);

  const { isLoading, error, data: welcomeData } = useWelcomeQuery();
  const { data: policiesData } = usePoliciesQuery();
  useAuthCheck(error as AxiosError);
  useGetCATBanner(!welcomeData);
  const { chatMaintenance } = useChatContext();
  const loadChat = !disableChat && chatMaintenance === false;

  const customerHasUbiPolicy =
    !!policiesData?.ubiEnrollment?.safeDriveEnrolled ||
    !!policiesData?.ubiEnrollment?.smartTrekEnrolled;

  const queryClient = useQueryClient();

  useEffect(() => {
    if (welcomeData) {
      setCookieWithExpiration('mpr_api_key', import.meta.env.VITE_X_API_KEY, {
        hours: 1,
      });

      queryClient.prefetchQuery({
        queryKey: ['policies'],
        queryFn: () => getPolicies(),
      });
    }
  }, [welcomeData]);

  return (
    <ChatProvider enabled={!!welcomeData}>
      <Stack
        data-testid="layout"
        direction="column"
        sx={{
          minHeight: '100vh',
        }}
      >
        {import.meta.env.NODE_ENV !== 'test' && <ScrollRestoration />}
        <Stack
          data-testid="layout"
          direction={{ mobile: 'column', desktop: 'row' }}
          sx={{
            minHeight: { desktop: 'calc(100vh - 111px)' },
          }}
        >
          <Button
            href="#skipLink"
            onBlur={() => setIsSkipLinkVisible(false)}
            onFocus={() => setIsSkipLinkVisible(true)}
            sx={{
              left: { mobile: '50%', desktop: 64 },
              top: {
                mobile: isSkipLinkVisible ? 8 : -50,
                desktop: isSkipLinkVisible ? 24 : -50,
              },
              position: 'fixed',
              zIndex: isSkipLinkVisible ? 10000 : 0,
              width: 'auto',
              borderRadius: 0,
              transform: { mobile: 'translateX(-50%)', desktop: 'none' },
              fontSize: { mobile: 16, tablet: 18 },
              px: 1,
              py: 0.5,
            }}
          >
            Skip to main content
          </Button>
          {welcomeData && <Header />}
          <div id="skipLink" />
          <Stack
            data-testid="layout"
            direction="column"
            sx={{
              width: 1,
              minHeight: '100vh',
              background: colors.csaaNeutralBackground,
            }}
          >
            {state.snackbars.length > 0 && (
              <Stack
                justifyContent="center"
                sx={{
                  px: { mobile: 2, tablet: 3, desktop: 7 },
                  py: 1.5,
                }}
                spacing={1.5}
              >
                {state.snackbars.map(({ key, ...snackbar }) => (
                  <Snackbar {...snackbar} key={key} id={key!} />
                ))}
              </Stack>
            )}
            <Main sx={{ width: 1, justifyContent: 'center' }}>
              {isLoading ? (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{ width: 1 }}
                >
                  <CircularProgress size={120} />
                </Stack>
              ) : welcomeData?.enableMaintenance ? (
                <Maintenance />
              ) : (
                <Outlet />
              )}
            </Main>
            <MyPolicyFooter customerHasUbiPolicy={customerHasUbiPolicy} />
          </Stack>
        </Stack>
        {welcomeData && !isLocal && <SessionTimeoutModal />}
        <PaperlessDialogs />
        {loadChat && <Chat />}
      </Stack>
    </ChatProvider>
  );
};

export default Layout;
